import { clearAccessToken, getAccessToken, setAccessToken } from '@/features/auth/lib/session';
import {
  clearImpersonationSession,
  getImpersonationSession,
  isImpersonating,
  setImpersonationAdminToken,
} from '@/features/auth/lib/impersonation-session';
import { getApiBaseUrl } from './api-base-url';

export const SESSION_EXPIRED_EVENT = 'foundry:session-expired';

function withAuthHeader(init: RequestInit | undefined): RequestInit {
  const token = getAccessToken();
  if (!token) return init ?? {};
  const headers = new Headers(init?.headers);
  if (!headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return { ...init, headers };
}

function handleSessionExpired(): void {
  clearAccessToken();
  clearImpersonationSession();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  }
}

function readAccessToken(body: unknown): string | null {
  const token = (body as { accessToken?: unknown }).accessToken;
  return typeof token === 'string' ? token : null;
}

// Refresh the ordinary session via the httpOnly refresh cookie.
async function attemptRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${getApiBaseUrl()}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return false;
    const token = readAccessToken(await res.json());
    if (token === null) return false;
    setAccessToken(token);
    return true;
  } catch {
    return false;
  }
}

type MintResult = { ok: true; token: string } | { ok: false; unauthorized: boolean };

// Re-mint an impersonation overlay token using the preserved admin token.
async function mintOverlayToken(targetId: string, adminToken: string): Promise<MintResult> {
  try {
    const res = await fetch(`${getApiBaseUrl()}/admin/users/${targetId}/impersonate/refresh`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${adminToken}` },
    });
    if (res.status === 401 || res.status === 403) return { ok: false, unauthorized: true };
    if (!res.ok) return { ok: false, unauthorized: false };
    const token = readAccessToken(await res.json());
    if (token === null) return { ok: false, unauthorized: false };
    return { ok: true, token };
  } catch {
    return { ok: false, unauthorized: false };
  }
}

async function refreshAdminToken(): Promise<string | null> {
  try {
    const res = await fetch(`${getApiBaseUrl()}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return null;
    return readAccessToken(await res.json());
  } catch {
    return null;
  }
}

// 'refreshed' → replay the request; 'expired' → the session is genuinely dead, sign out;
// 'transient' → a recoverable blip, surface this request's 401 but keep the session intact.
type RefreshOutcome = 'refreshed' | 'expired' | 'transient';

// While impersonating, an expired overlay token is re-minted from the admin's session — never
// from /auth/refresh directly, which would silently swap the admin back in. If the preserved
// admin token has itself aged out, refresh it via its cookie first, then re-mint.
async function attemptImpersonationRefresh(): Promise<RefreshOutcome> {
  const session = getImpersonationSession();
  // Impersonation ended (e.g. Stop) while this refresh was in flight — refresh the admin
  // session normally instead of tearing down the just-restored admin session.
  if (session === null) return (await attemptRefresh()) ? 'refreshed' : 'expired';
  let result = await mintOverlayToken(session.targetId, session.adminToken);
  if (!result.ok && result.unauthorized) {
    const adminToken = await refreshAdminToken();
    if (adminToken === null) return 'expired';
    setImpersonationAdminToken(adminToken);
    result = await mintOverlayToken(session.targetId, adminToken);
  }
  if (result.ok) {
    setAccessToken(result.token);
    return 'refreshed';
  }
  // A 401/403 means the admin genuinely can't mint (dead session) → sign out. Any other failure
  // (5xx, network) is a transient blip: keep the preserved admin session so the next action just
  // retries, rather than forcing a re-login over an unrelated hiccup.
  return result.unauthorized ? 'expired' : 'transient';
}

// Single-flight, keyed on impersonation state: concurrent 401s of the same kind share one
// refresh (else N rotations would trip reuse-detection), but a state change (start/stop)
// starts a fresh one so a wrong-branch token is never written into the access-token slot.
let inFlightRefresh: { impersonating: boolean; promise: Promise<RefreshOutcome> } | null = null;

function refreshSession(): Promise<RefreshOutcome> {
  const impersonating = isImpersonating();
  if (inFlightRefresh?.impersonating !== impersonating) {
    const run = impersonating
      ? attemptImpersonationRefresh()
      : attemptRefresh().then((ok): RefreshOutcome => (ok ? 'refreshed' : 'expired'));
    const promise = run.finally(() => {
      if (inFlightRefresh?.promise === promise) {
        inFlightRefresh = null;
      }
    });
    inFlightRefresh = { impersonating, promise };
  }
  return inFlightRefresh.promise;
}

// Attaches the bearer token; on 401, refreshes once and replays the request, falling back to
// SESSION_EXPIRED_EVENT only if that fails.
export async function authFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const response = await fetch(input, withAuthHeader(init));
  if (response.status !== 401) return response;

  const outcome = await refreshSession();
  if (outcome === 'expired') {
    handleSessionExpired();
    return response;
  }
  // A transient refresh blip leaves the session untouched: surface this request's 401 (the
  // caller can retry) without forcing a re-login.
  if (outcome === 'transient') {
    return response;
  }

  const retry = await fetch(input, withAuthHeader(init));
  if (retry.status === 401) {
    handleSessionExpired();
  }
  return retry;
}
